import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { AnalyticsFilters, PRESETS, Preset, rangeLabel } from './filters';

export interface FilterOptions {
  lines: { id: number; name: string; external_code: string | null; has_telemetry: boolean }[];
  materials: { id: number; name: string; has_density: boolean }[];
  timezone: string;
}

interface Props {
  filters: AnalyticsFilters;
  onChange: (patch: Partial<AnalyticsFilters>) => void;
  /** Hide the "compare with previous period" toggle on screens that do not compare. */
  showCompare?: boolean;
  /** Hide the material select where it makes no sense (e.g. line history). */
  showMaterial?: boolean;
}

// Shared filter bar: period presets, custom range, line, material, shift. Options come from
// /analytics/filters once per mount; the state itself lives in the URL (see filters.ts).
const AnalyticsFilterBar: React.FC<Props> = ({ filters, onChange, showCompare = true, showMaterial = true }) => {
  const { t } = useLanguage();
  const [options, setOptions] = useState<FilterOptions | null>(null);

  useEffect(() => {
    api.get('/analytics/filters').then(res => setOptions(res.data)).catch(() => setOptions({ lines: [], materials: [], timezone: 'Europe/Warsaw' }));
  }, []);

  return (
    <div style={bar}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        <div style={presetGroup}>
          {PRESETS.map((p: Preset) => (
            <button
              key={p}
              type="button"
              onClick={() => onChange({ preset: p })}
              style={{ ...presetBtn, ...(filters.preset === p ? presetActive : {}) }}
            >
              {t(`analytics.preset_${p}`)}
            </button>
          ))}
        </div>
        <span style={{ fontSize: 13, color: '#64748b', marginLeft: 8, whiteSpace: 'nowrap' }}>{rangeLabel(filters)}</span>
        {filters.preset === 'custom' && (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginLeft: 8 }}>
            <input type="date" value={filters.customFrom} onChange={e => onChange({ customFrom: e.target.value })} style={inp} />
            <span style={{ color: '#94a3b8' }}>–</span>
            <input type="date" value={filters.customTo} onChange={e => onChange({ customTo: e.target.value })} style={inp} />
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <label style={lbl}>{t('analytics.filterLine')}
          <select value={filters.lineId} onChange={e => onChange({ lineId: e.target.value })} style={inp}>
            <option value="">{t('analytics.all')}</option>
            {options?.lines.map(l => (
              <option key={l.id} value={l.id}>{l.name}{l.has_telemetry ? '' : ` (${t('analytics.noTelemetry')})`}</option>
            ))}
          </select>
        </label>
        {showMaterial && (
          <label style={lbl}>{t('analytics.filterMaterial')}
            <select value={filters.materialId} onChange={e => onChange({ materialId: e.target.value })} style={inp}>
              <option value="">{t('analytics.all')}</option>
              {options?.materials.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              <option value="0">{t('analytics.unknownMaterial')}</option>
            </select>
          </label>
        )}
        <label style={lbl}>{t('analytics.filterShift')}
          <select value={filters.shift} onChange={e => onChange({ shift: e.target.value as AnalyticsFilters['shift'] })} style={inp}>
            <option value="">{t('analytics.all')}</option>
            <option value="zmiana_1">{t('analytics.shift1')}</option>
            <option value="zmiana_2">{t('analytics.shift2')}</option>
          </select>
        </label>
        {showCompare && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569', paddingBottom: 8 }}>
            <input type="checkbox" checked={filters.compare} onChange={e => onChange({ compare: e.target.checked })} />
            {t('analytics.compare')}
          </label>
        )}
      </div>
    </div>
  );
};

const bar: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 10, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', marginBottom: 20 };
const presetGroup: React.CSSProperties = { display: 'inline-flex', background: '#f1f5f9', borderRadius: 8, padding: 3, gap: 2, flexWrap: 'wrap' };
const presetBtn: React.CSSProperties = { border: 'none', background: 'transparent', padding: '6px 12px', borderRadius: 6, fontSize: 13, color: '#475569', cursor: 'pointer', fontWeight: 500 };
const presetActive: React.CSSProperties = { background: '#fff', color: '#1e293b', fontWeight: 700, boxShadow: '0 1px 2px rgba(0,0,0,0.08)' };
const lbl: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 0.5, textTransform: 'uppercase' };
const inp: React.CSSProperties = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff', minWidth: 140 };

export default AnalyticsFilterBar;
